import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useEffectOnce } from 'react-use'
import { InfoList } from 'views/components'
import { reportingApiEvents, reportingApiSelectors } from 'modules/api/reporting'
import { agencyApiEvents, agencyApiSelectors } from 'modules/api/agency'
import { getAgencyHeading } from 'utils/agency'
import { formatStandard } from 'utils/date'
import { SuspenseReference } from '../../types'
import { getClickUpUrl } from '../../utils'
import styles from './ViewSuspenseAccount.module.scss'

interface ViewAllocatedReferenceProps {
  currentReference?: SuspenseReference | null
}

const ViewAllocatedReference = ({ currentReference }: ViewAllocatedReferenceProps): React.ReactElement => {
  const dispatch = useDispatch()
  const allocatedToRef = currentReference?.allocatedToRef
  const clickUpRef = currentReference?.clickUpRef
  const clickUpUrl = getClickUpUrl(clickUpRef)

  useEffectOnce(() => {
    dispatch(agencyApiEvents.agencyListing_request())
  })

  useEffect(() => {
    // `reference` is a path segment on the lookup.
    if (allocatedToRef) {
      dispatch(reportingApiEvents.suspenseReferenceLookup_request({ reference: allocatedToRef }))
    }
  }, [dispatch, allocatedToRef])

  const referenceLookup = useSelector(state => reportingApiSelectors.getSuspenseReferenceLookup(state)(allocatedToRef))
  const agency = useSelector(state => agencyApiSelectors.getAgencyFromListingById(state)(referenceLookup?.agencyId))

  const infoItems = [
    { label: 'Wallet number', value: allocatedToRef },
    { label: 'Account type', value: referenceLookup?.accountType },
    { label: 'Contact', value: referenceLookup?.contactName ?? currentReference?.contactName },
    { label: 'Agency', value: agency ? getAgencyHeading(agency) : undefined },
    { label: 'Property', value: referenceLookup?.propertyAddress },
    { label: 'Allocated by', value: currentReference?.allocatedByName ?? currentReference?.allocatedBy },
    { label: 'Allocated on', value: formatStandard(currentReference?.allocatedAt) },
    { label: 'Received', value: formatStandard(currentReference?.dateReceived) },
    {
      label: 'ClickUp ticket',
      value: clickUpUrl ? (
        <a href={clickUpUrl} target="_blank" rel="noopener noreferrer">
          {clickUpRef}
        </a>
      ) : (
        clickUpRef
      ),
    },
  ]

  const noteAuthorship = [
    currentReference?.notesUpdatedBy,
    currentReference?.notesUpdatedAt && formatStandard(currentReference.notesUpdatedAt),
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div>
      <span className={styles.eyebrow}>Allocation details</span>
      <InfoList items={infoItems} spacing="lg" />
      {/* Read-only once allocated. */}
      <div className={styles.notes}>
        <span className={styles.eyebrow}>Notes</span>
        {currentReference?.notes ? (
          <p className={styles.note}>
            {currentReference.notes}
            {noteAuthorship && <small>{noteAuthorship}</small>}
          </p>
        ) : (
          <p className={styles.empty}>No notes.</p>
        )}
      </div>
    </div>
  )
}

export default ViewAllocatedReference
